import React, { useState, useCallback } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  Text,
  Alert,
} from '@mantine/core';
import { IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { SettingDataType, SettingVisibility, useUpsertSettingMutation } from 'generated/graphql';

interface SettingCreateModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const DATA_TYPE_OPTIONS = [
  { value: 'STRING', label: 'String' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'INTEGER', label: 'Integer' },
  { value: 'DECIMAL', label: 'Decimal' },
  { value: 'DATE_TIME', label: 'DateTime' },
  { value: 'JSON', label: 'JSON' },
  { value: 'STRING_LIST', label: 'String List' },
];

const VISIBILITY_OPTIONS = [
  { value: 'PUBLIC', label: 'Public', description: 'Anyone can read' },
  { value: 'PROTECTED', label: 'Protected', description: 'Authenticated users only' },
  { value: 'INTERNAL', label: 'Internal', description: 'Backend only' },
  { value: 'SYSTEM', label: 'System', description: 'Read-only, deployment managed' },
];

export function SettingCreateModal({ opened, onClose, onSuccess }: SettingCreateModalProps) {
  const [path, setPath] = useState('');
  const [value, setValue] = useState('');
  const [dataType, setDataType] = useState<string>('STRING');
  const [visibility, setVisibility] = useState<string>('INTERNAL');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const [upsertSetting, { loading }] = useUpsertSettingMutation();

  const resetForm = useCallback(() => {
    setPath('');
    setValue('');
    setDataType('STRING');
    setVisibility('INTERNAL');
    setDescription('');
    setValidationError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const validatePath = (p: string): string | null => {
    if (!p) return 'Path is required';
    if (!p.startsWith('/')) return 'Path must start with /';
    if (p.includes('//')) return 'Path cannot contain consecutive slashes';
    if (p.endsWith('/') && p.length > 1) return 'Path cannot end with /';
    return null;
  };

  const validateValue = (v: string, dt: string): string | null => {
    if (!v && dt !== 'STRING') return 'Value is required';

    switch (dt) {
      case 'BOOLEAN':
        if (!['true', 'false'].includes(v.toLowerCase())) {
          return 'Boolean must be "true" or "false"';
        }
        break;
      case 'INTEGER':
        if (!/^-?\d+$/.test(v)) {
          return 'Integer must be a whole number';
        }
        break;
      case 'DECIMAL':
        if (!/^-?\d+(\.\d+)?$/.test(v)) {
          return 'Decimal must be a valid number';
        }
        break;
      case 'JSON':
        try {
          JSON.parse(v);
        } catch {
          return 'Invalid JSON format';
        }
        break;
      case 'STRING_LIST':
        try {
          const arr = JSON.parse(v);
          if (!Array.isArray(arr) || !arr.every(item => typeof item === 'string')) {
            return 'Must be a JSON array of strings';
          }
        } catch {
          return 'Invalid JSON array format';
        }
        break;
    }
    return null;
  };

  const handleSubmit = async () => {
    // Validate path
    const pathError = validatePath(path);
    if (pathError) {
      setValidationError(pathError);
      return;
    }

    // Validate value
    const valueError = validateValue(value, dataType);
    if (valueError) {
      setValidationError(valueError);
      return;
    }

    setValidationError(null);

    try {
      await upsertSetting({
        variables: {
          input: {
            path,
            value,
            dataType: dataType as SettingDataType,
            visibility: visibility as SettingVisibility,
            description: description || undefined,
          },
        },
      });

      notifications.show({
        title: 'Setting Created',
        message: `Setting "${path}" has been created successfully.`,
        color: 'green',
      });

      handleClose();
      onSuccess?.();
    } catch (error: any) {
      const message = error?.message || 'Failed to create setting';
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
      setValidationError(message);
    }
  };

  const getValuePlaceholder = () => {
    switch (dataType) {
      case 'BOOLEAN': return 'true or false';
      case 'INTEGER': return '12345';
      case 'DECIMAL': return '123.45';
      case 'DATE_TIME': return '2024-01-15T10:30:00Z';
      case 'JSON': return '{"key": "value"}';
      case 'STRING_LIST': return '["item1", "item2"]';
      default: return 'Enter value';
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Create New Setting"
      size="lg"
    >
      <Stack gap="md">
        {validationError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {validationError}
          </Alert>
        )}

        <TextInput
          label="Path"
          placeholder="/queues/ranked/enabled"
          description="Hierarchical path using forward slashes"
          value={path}
          onChange={(e) => setPath(e.currentTarget.value)}
          required
          error={path && validatePath(path)}
        />

        <Select
          label="Data Type"
          data={DATA_TYPE_OPTIONS}
          value={dataType}
          onChange={(v) => v && setDataType(v)}
          required
        />

        <Select
          label="Visibility"
          data={VISIBILITY_OPTIONS}
          value={visibility}
          onChange={(v) => v && setVisibility(v)}
          description={VISIBILITY_OPTIONS.find(o => o.value === visibility)?.description}
          required
        />

        {dataType === 'JSON' || dataType === 'STRING_LIST' ? (
          <Textarea
            label="Value"
            placeholder={getValuePlaceholder()}
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            minRows={3}
            required
            error={value && validateValue(value, dataType)}
          />
        ) : (
          <TextInput
            label="Value"
            placeholder={getValuePlaceholder()}
            value={value}
            onChange={(e) => setValue(e.currentTarget.value)}
            required={dataType !== 'STRING'}
            error={value && validateValue(value, dataType)}
          />
        )}

        <Textarea
          label="Description"
          placeholder="Human-readable description for admin UI"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          minRows={2}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            leftSection={<IconPlus size={16} />}
            className="bg-blue-500 hover:bg-blue-600"
          >
            Create Setting
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
